import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Wand2, Check, Shuffle, ChevronUp, ChevronDown, X, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BLOCK_META } from "@/lib/ui";
import { isoDate } from "@/lib/time";

const STRATEGIES = ["balanced", "deep_first", "quick_first"];
const STRAT_LABEL = { balanced: "Équilibré", deep_first: "Deep work d'abord", quick_first: "Quick wins d'abord" };
function toHHMM(min) { min = Math.max(0, Math.min(1439, min)); return `${String(Math.floor(min / 60)).padStart(2, "0")}h${String(min % 60).padStart(2, "0")}`; }

// "Plan My Day" proposal mode: AIRTIME suggests FLEXIBLE blocks around FIXED events, TaskBlocks & personal time.
// Accepting turns suggestions into real TaskBlocks (audited as USER-accepted AIRTIME suggestions).
export default function PlanDayView({ day, onApplied }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [strategyIdx, setStrategyIdx] = useState(0);
  const dateStr = isoDate(day);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get("/plan/day", { params: { date_str: dateStr, strategy: STRATEGIES[strategyIdx] } });
    setBlocks(res.data.blocks.map((b, i) => ({ ...b, _key: `${i}-${b.start}` })));
    setLoading(false);
  }, [dateStr, strategyIdx]);
  useEffect(() => { load(); }, [load]);

  const moveBlock = (key, delta) => setBlocks((prev) => prev.map((b) => {
    if (b._key !== key || b.type !== "FLEXIBLE") return b;
    const dur = b.end_min - b.start_min; const ns = b.start_min + delta;
    return { ...b, start_min: ns, end_min: ns + dur, start: toHHMM(ns), end: toHHMM(ns + dur) };
  }).sort((a, b) => a.start_min - b.start_min));

  const accept = async () => {
    setAccepting(true);
    try {
      const flex = blocks.filter((b) => b.type === "FLEXIBLE" && b.task_id);
      for (const b of flex) {
        const s = new Date(`${dateStr}T${b.start.replace("h", ":")}:00`), e = new Date(`${dateStr}T${b.end.replace("h", ":")}:00`);
        await api.post(`/tasks/${b.task_id}/blocks`, { start: s.toISOString(), end: e.toISOString() });
      }
      toast.success(`${flex.length} bloc(s) de tâche planifié(s)`);
      onApplied?.(); load();
    } catch { toast.error("Impossible d'appliquer le plan"); } finally { setAccepting(false); }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="plan-day-view">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="text-sm text-muted-foreground">Proposition AIRTIME — rien n'est appliqué sans ton accord. <span className="font-mono text-[10px] uppercase tracking-widest">Stratégie : {STRAT_LABEL[STRATEGIES[strategyIdx]]}</span></div>
        <div className="flex gap-2">
          <button data-testid="calendar-replan" onClick={() => setStrategyIdx((i) => (i + 1) % STRATEGIES.length)} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:bg-accent"><Shuffle className="w-4 h-4" /> Planifie autrement</button>
          <button data-testid="calendar-accept-plan" onClick={accept} disabled={accepting || !blocks.some((b) => b.type === "FLEXIBLE")} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground font-medium disabled:opacity-50">{accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Appliquer le plan</button>
        </div>
      </div>
      {loading ? <div className="h-40 flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div> : blocks.length === 0 ? (
        <div className="flex flex-col items-center py-14 text-center"><Wand2 className="w-8 h-8 text-muted-foreground/40 mb-3" /><div className="text-sm text-muted-foreground">Journée libre — aucun bloc requis.</div></div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence initial={false}>{blocks.map((b) => (
            <motion.div key={b._key} layout initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, height: 0 }} data-testid={`calendar-block-${b._key}`} className="flex items-stretch gap-4 group">
              <div className="w-16 shrink-0 text-right font-mono text-xs text-muted-foreground pt-3">{b.start}</div>
              <div className={`flex-1 rounded-xl px-4 py-3 ${BLOCK_META[b.type] || BLOCK_META.FIXED}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium flex items-center gap-1.5">{b.locked && <Lock className="w-3 h-3" />}{b.title}</div>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-wider opacity-70">{b.type === "FLEXIBLE" ? "Proposition" : b.type === "TASK" ? "Tâche planifiée" : b.type === "PERSONAL" ? "Personnel" : "Fixe"}{b.tentative ? " · provisoire" : ""}</span>
                    {b.type === "FLEXIBLE" && (
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => moveBlock(b._key, -15)} className="p-1 rounded hover:bg-white/10"><ChevronUp className="w-3.5 h-3.5" /></button>
                        <button onClick={() => moveBlock(b._key, 15)} className="p-1 rounded hover:bg-white/10"><ChevronDown className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setBlocks((p) => p.filter((x) => x._key !== b._key))} className="p-1 rounded hover:bg-white/10 hover:text-rose-300"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="font-mono text-[11px] opacity-70 mt-0.5">{b.start} — {b.end}{b.project ? ` · ${b.project}` : ""}</div>
              </div>
            </motion.div>
          ))}</AnimatePresence>
        </div>
      )}
    </div>
  );
}
